// Реестр разделов. Весь веб — семь разделов, ни одним больше (ПРАВИЛА.md, раздел 1.1):
// новая сущность — это представление в существующем разделе или вкладка в карточке,
// а не новая строка здесь.
//
// Видимость раздела — код права из contracts/enums/permissions.yaml. Права приходят готовым
// списком в UserPayload.permissions; сопоставление «право есть → раздел виден» делает
// visibleSections из shared/rbac/permissions.ts. Изоляция данных при этом живёт на сервере,
// выборкой: скрытие раздела — удобство навигации, а не защита.

export type SectionSlug =
  'money' | 'people' | 'sites' | 'payroll' | 'cases' | 'hiring' | 'settings';

export interface SectionDef {
  readonly slug: SectionSlug;
  /** Название в полосе разделов. */
  readonly title: string;
  /**
   * Дополнительное условие: раздел виден, только если есть ХОТЬ ОДНО право и отсюда тоже.
   *
   * Заведено 02.09.2026 по прогону девяти ролей. «Дела» — единственный раздел, который не
   * ходит за своими данными сам: он собирает очередь из ЧУЖИХ выборок (подотчёт, реестр
   * людей, официальный контур), и это осознанное решение — число в деле обязано совпасть с
   * числом строк там, куда дело ведёт. Обратная сторона: право на раздел и права на источники
   * — разные, и у четырёх ролей они разошлись. Рекрутёр, табельщик, заместитель по охране и
   * старший объекта входили в программу и упирались в красную ошибку «не удалось загрузить»,
   * причём у рекрутёра это был ПЕРВЫЙ экран после входа: раздел «Дела» стоял у него первым в
   * полосе.
   *
   * Одного списка `permissions` тут мало: он «любое из», а нужно «своё право И хоть один
   * источник». Поле необязательное и на сегодня стоит ровно у одного раздела — заводить его
   * у второго значит, что и второй собирает данные не своими руками, а это уже разговор.
   */
  readonly alsoAnyOf?: readonly string[];
  /**
   * Права, при которых раздел виден: достаточно ЛЮБОГО из списка.
   *
   * Список, а не одно право, потому что в разделе живёт несколько представлений, и они не
   * обязаны принадлежать одной роли. Живой случай: у табельщика есть права на табель, но нет
   * права на ведомости — с одним правом на раздел он не увидел бы «Расчёт» вовсе, то есть не
   * нашёл бы собственную работу. У старшего объекта то же самое.
   */
  readonly permissions: readonly string[];
  /**
   * Представление по умолчанию, ПО ПРАВАМ. Первое, до чьего права человек дотянулся.
   *
   * Одного значения мало по той же причине: раздел откроется, а экран внутри будет отказом.
   */
  readonly defaultViews: readonly { readonly view: string; readonly permission: string }[];
  /** true, пока у раздела нет настоящих экранов: каркас показывает честную заглушку. */
  readonly notReady: boolean;
}

/** Порядок массива — порядок в полосе разделов и порядок отката «первый доступный». */
export const SECTIONS: readonly SectionDef[] = [
  {
    slug: 'money',
    // «Суточные расходы» — слова самого заказчика (29.09.2026: «вкладка слева, так и
    // напишите»). Раздел раньше назывался «Деньги»; адрес `/money/…` и право
    // `core.accountable.view` остались прежними: переименование касается только подписи.
    title: 'Суточные расходы',
    permissions: ['core.accountable.view'],
    // Утро финансового директора: ноль кликов до подотчёта, отсортированного по остатку.
    defaultViews: [{ view: 'accountable', permission: 'core.accountable.view' }],
    notReady: false,
  },
  {
    slug: 'people',
    title: 'Люди',
    permissions: ['core.employee.view'],
    // Реестр людей; рядом с ним срезы по типу оформления, они приходят с сервера.
    defaultViews: [{ view: 'employees', permission: 'core.employee.view' }],
    notReady: false,
  },
  {
    slug: 'sites',
    // Право видимости раздела обязано совпадать с правом, по которому сервер отдаёт его
    // данные. С правом на табель раздел был бы виден полевому руководителю, а список
    // объектов сервер ему не отдаёт — человек получал бы отказ вместо экрана.
    title: 'Объекты',
    permissions: ['core.dictionary.view'],
    // Реестр объектов; рядом с ним представления «Заказчики» и «Посты».
    defaultViews: [{ view: 'objects', permission: 'core.dictionary.view' }],
    notReady: false,
  },
  {
    slug: 'payroll',
    title: 'Расчёт',
    // Два права, и это не запас на будущее. У табельщика есть core.timesheet.*, но НЕТ
    // core.payroll.view: с одним правом на раздел он не увидел бы «Расчёт» вовсе и не нашёл
    // бы собственную работу. У старшего объекта та же беда, и у него вдобавок нет права на
    // «Объекты» — то есть до табеля ему не добраться ниоткуда.
    // Четвёртое право — панель показателей. Она открыта всем ролям, кроме кадров (тз, раздел
    // 3), но живёт внутри «Расчёта» представлением «Сводка», и до 06.09.2026 рекрутёр не мог
    // до неё добраться: ни ведомостей, ни табеля, ни контура у него нет, а значит раздел в
    // полосе не появлялся вовсе. Панель показателей была ему обещана и не показана нигде.
    permissions: [
      'core.payroll.view',
      'core.timesheet.view',
      'core.official.view',
      'core.dashboard.view',
    ],
    // Порядок важен: у кого есть ведомости, тот попадает на ведомости; у кого только табель —
    // сразу в табель, а не на экран отказа внутри открывшегося раздела. Официальный контур
    // стоит последним: право на него есть у финотдела и руководства, а у них есть и ведомости,
    // и открывать людям контур вместо привычного экрана незачем. Но у того, у кого ЕСТЬ только
    // контур, раздел всё равно откроется — иначе он не нашёл бы свою работу.
    // Сводка стоит ПОСЛЕДНЕЙ: у кого есть своя работа — ведомости, табель, контур, — тот
    // попадает на неё, а не на отчёты. Но у кого нет ничего, кроме панели показателей,
    // раздел всё равно откроется на ней, а не экраном отказа.
    defaultViews: [
      { view: 'sheets', permission: 'core.payroll.view' },
      { view: 'timesheet', permission: 'core.timesheet.view' },
      { view: 'official', permission: 'core.official.view' },
      { view: 'summary', permission: 'core.dashboard.view' },
    ],
    notReady: false,
  },
  {
    slug: 'cases',
    title: 'Дела',
    permissions: ['core.dashboard.view'],
    // Очередь собирается из трёх чужих выборок. Нет права ни на одну — очереди не из чего
    // собраться, и раздел в полосе не появляется: пустой он честнее не станет, а рекрутёру,
    // у которого нет ни подотчёта, ни реестра людей, ни контура, он не даёт ничего вовсе.
    alsoAnyOf: ['core.accountable.view', 'core.employee.view', 'core.official.view'],
    defaultViews: [{ view: 'list', permission: 'core.dashboard.view' }],
    notReady: false,
  },
  // Раздел найма ведёт соседняя сессия (web/src/modules/hiring/); здесь ровно одна строка.
  {
    slug: 'hiring',
    title: 'Найм',
    // Четыре права, а не одно, и это тот самый случай, ради которого поле объявлено списком.
    //
    // По матрице тз (раздел 3) в этом разделе живут ЧЕТЫРЕ разные области с разной раздачей:
    // кандидаты (рекрутёр — полный, кадры — ред., руководство — просмотр, финотдел — НЕТ),
    // единый чёрный список (финотдел и кадры — просмотр, администратор — полный), учётные
    // записи площадки и помощник переписки (администратор — настройка). С одним правом на
    // раздел финансовый отдел не увидел бы чёрный список, который ему положен, а
    // администратор не увидел бы раздела вовсе — при том что подключать три учётные записи
    // «Авито» поручено именно ему.
    permissions: [
      'hiring.candidate.view',
      'hiring.blacklist.view',
      'hiring.avito.view',
      'hiring.assistant.view',
    ],
    // Порядок — порядок отката «первое, до чьего права дотянулся». Рекрутёр и кадры попадают
    // на кандидатов, финотдел — сразу на чёрный список, и это верно: кандидаты ему закрыты.
    defaultViews: [
      { view: 'candidates', permission: 'hiring.candidate.view' },
      { view: 'blacklist', permission: 'hiring.blacklist.view' },
      { view: 'accounts', permission: 'hiring.avito.view' },
      { view: 'dialogs', permission: 'hiring.assistant.view' },
    ],
    // Экраны раздела появились 02.09.2026: воронка кандидатов с карточкой, аналитика воронки,
    // чёрный список с предложениями из выгрузки и журналом сверок, площадка «Авито» с
    // доступами, вакансиями и журналом обмена, переписка помощника и библиотека ответов.
    notReady: false,
  },
  {
    slug: 'settings',
    title: 'Настройки',
    permissions: ['core.setting.view'],
    defaultViews: [{ view: 'general', permission: 'core.setting.view' }],
    // Раздел живой: в нём настраивается адрес сервера — то, без чего программа у заказчика
    // не находит данные. Роли и справочники придут с блоком Б9, и об этом сказано на экране.
    notReady: false,
  },
];

export function sectionBySlug(slug: string): SectionDef | undefined {
  return SECTIONS.find((section) => section.slug === slug);
}
